import { Tour } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageAccess } from '../../hooks/usePageAccess';
import useTranslation from '../../localization/useTranslation';
import {
  flattenSidebarTourPages,
  tourMenuTargetId,
} from '../../utilities/sidebarMenu';

export const ONBOARDING_STORAGE_KEY = 'dentrist-onboarding-completed';
export const ONBOARDING_RESTART_EVENT = 'dentrist:start-onboarding';
export const OPEN_MOBILE_MENU_EVENT = 'dentrist:open-mobile-menu';
export const CLOSE_MOBILE_MENU_EVENT = 'dentrist:close-mobile-menu';

const normalizePath = (path) => (path ? path.replace(/\/$/, '').toLowerCase() : '');

const isMobileShell = () =>
  typeof window !== 'undefined' && window.matchMedia('(max-width: 991px)').matches;

const waitForTargets = (getters, { attempts = 30, intervalMs = 120 } = {}) =>
  new Promise((resolve) => {
    let tries = 0;
    const tick = () => {
      const ready = getters.every((get) => {
        try {
          return Boolean(get());
        } catch {
          return false;
        }
      });
      if (ready || tries >= attempts) {
        resolve(ready);
        return;
      }
      tries += 1;
      window.setTimeout(tick, intervalMs);
    };
    tick();
  });

const resolvePageLabel = (page, t) => {
  if (!page) {
    return '';
  }

  const candidates = [];
  if (typeof page?.meta?.translationKey === 'string') {
    candidates.push(page.meta.translationKey);
  }
  if (typeof page?.translation_key === 'string') {
    candidates.push(page.translation_key);
  }
  if (typeof page?.name === 'string' && page.name.trim().length > 0) {
    const rawName = page.name.trim();
    candidates.push(`sidebar.${rawName}`);
    candidates.push(`sidebar.${rawName.replace(/\./g, '_')}`);
  }

  for (const key of candidates) {
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
  }

  return page.title ?? page.name ?? '';
};

/**
 * Guided system tour for the authenticated portal.
 * Walks chrome controls, then every visible sidebar page.
 * Auto-starts once for first-time users; replay from the profile menu.
 */
const OnboardingTour = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { menu, user } = usePageAccess();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [busy, setBusy] = useState(false);
  const returnPathRef = useRef(null);
  const stepsRef = useRef([]);

  const tourPages = useMemo(() => flattenSidebarTourPages(menu, user), [menu, user]);

  const markCompleted = useCallback(() => {
    try {
      window.localStorage.setItem(ONBOARDING_STORAGE_KEY, '1');
    } catch {
      // Ignore storage failures.
    }
  }, []);

  const steps = useMemo(() => {
    const intro = [
      {
        title: t('onboarding.welcome.title'),
        description: t('onboarding.welcome.description'),
        target: () => document.getElementById('tour-sidebar'),
        placement: 'right',
        needsMobileMenu: true,
      },
      {
        title: t('onboarding.navigation.title'),
        description: t('onboarding.navigation.description'),
        target: () => document.querySelector('#tour-sidebar .sidebar-menu'),
        placement: 'right',
        needsMobileMenu: true,
      },
      {
        title: t('onboarding.controls.title'),
        description: t('onboarding.controls.description'),
        target: () => document.getElementById('tour-header-controls'),
        placement: 'bottom',
        needsMobileMenu: false,
      },
      {
        title: t('onboarding.account.title'),
        description: t('onboarding.account.description'),
        target: () => document.getElementById('tour-navbar'),
        placement: 'bottomLeft',
        needsMobileMenu: false,
      },
      {
        title: t('onboarding.pages.title'),
        description: t('onboarding.pages.description'),
        target: () => document.getElementById('tour-content'),
        placement: 'top',
        needsMobileMenu: false,
      },
    ];

    const pageSteps = tourPages.map((page) => {
      const label = resolvePageLabel(page, t);
      return {
        title: label,
        description: t('onboarding.page.description', { page: label }),
        path: page.path,
        menuName: page.name,
        needsMobileMenu: true,
        target: () => {
          const menuEl = document.getElementById(tourMenuTargetId(page.name));
          if (menuEl) {
            return menuEl;
          }
          return document.getElementById('tour-content');
        },
        placement: 'right',
      };
    });

    const outro = [
      {
        title: t('onboarding.finish.title'),
        description: t('onboarding.finish.description'),
        target: () => document.getElementById('tour-content'),
        placement: 'top',
        needsMobileMenu: false,
      },
    ];

    return [...intro, ...pageSteps, ...outro];
  }, [t, tourPages]);

  stepsRef.current = steps;

  const prepareStep = useCallback(async (index) => {
    const step = stepsRef.current[index];
    if (!step) {
      return false;
    }

    const navigated =
      Boolean(step.path) &&
      normalizePath(window.location.pathname) !== normalizePath(step.path);

    if (navigated) {
      navigate(step.path);
      // PrivateRoute closes the mobile drawer on pathname change — wait for that, then reopen.
      await new Promise((resolve) => window.setTimeout(resolve, 80));
    }

    if (isMobileShell()) {
      window.dispatchEvent(
        new Event(step.needsMobileMenu ? OPEN_MOBILE_MENU_EVENT : CLOSE_MOBILE_MENU_EVENT)
      );
      if (step.needsMobileMenu) {
        await new Promise((resolve) => window.setTimeout(resolve, 120));
      }
    }

    const ready = await waitForTargets([
      () => {
        try {
          return Boolean(step.target?.());
        } catch {
          return false;
        }
      },
    ]);

    if (step.path) {
      // Give lazy route chunks a beat to paint after the target exists.
      await new Promise((resolve) => window.setTimeout(resolve, 180));
    }

    return ready;
  }, [navigate]);

  const startTour = useCallback(async () => {
    if (busy) {
      return;
    }
    setBusy(true);
    try {
      returnPathRef.current = `${window.location.pathname}${window.location.search}`;
      const shellReady = await waitForTargets([
        () => document.getElementById('tour-sidebar'),
        () => document.getElementById('tour-header-controls'),
        () => document.getElementById('tour-navbar'),
        () => document.getElementById('tour-content'),
      ]);
      if (!shellReady) {
        return;
      }
      await prepareStep(0);
      setCurrent(0);
      setOpen(true);
    } finally {
      setBusy(false);
    }
  }, [busy, prepareStep]);

  useEffect(() => {
    let cancelled = false;

    const maybeAutoStart = async () => {
      try {
        const done = window.localStorage.getItem(ONBOARDING_STORAGE_KEY);
        if (!done && !cancelled) {
          await startTour();
        }
      } catch {
        if (!cancelled) {
          await startTour();
        }
      }
    };

    maybeAutoStart();

    const onRestart = () => {
      startTour();
    };
    window.addEventListener(ONBOARDING_RESTART_EVENT, onRestart);

    return () => {
      cancelled = true;
      window.removeEventListener(ONBOARDING_RESTART_EVENT, onRestart);
    };
  }, [startTour]);

  const handleClose = useCallback(() => {
    setOpen(false);
    markCompleted();
    if (isMobileShell()) {
      window.dispatchEvent(new Event(CLOSE_MOBILE_MENU_EVENT));
    }
    const returnPath = returnPathRef.current;
    if (returnPath && normalizePath(returnPath) !== normalizePath(window.location.pathname + window.location.search)) {
      navigate(returnPath);
    }
    returnPathRef.current = null;
  }, [markCompleted, navigate]);

  const handleChange = useCallback(async (next) => {
    if (busy) {
      return;
    }
    setBusy(true);
    setOpen(false);
    try {
      await prepareStep(next);
      setCurrent(next);
      setOpen(true);
    } finally {
      setBusy(false);
    }
  }, [busy, prepareStep]);

  return (
    <Tour
      open={open}
      current={current}
      onChange={handleChange}
      onClose={handleClose}
      onFinish={handleClose}
      steps={steps}
      type="primary"
    />
  );
};

export const startOnboardingTour = () => {
  if (typeof window === 'undefined') {
    return;
  }
  window.dispatchEvent(new Event(ONBOARDING_RESTART_EVENT));
};

export default OnboardingTour;
